import { useCallback, useEffect, useRef, useState } from 'react'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'

export type UsePromiseValueOptions<T> = {
    initialValue: T
    enabled?: boolean
    clearValueWhenDisabled?: boolean
}

export type UsePromiseValueResult<T> = {
    value: T
    loading: boolean
    error: Nullable<Error>
    resolve: () => Promise<void>
    refresh: () => Promise<void>
}

/**
 * Runs an async request and stores its resolved value in local hook state.
 *
 * Similar in spirit to React 19 `use(promise)`, but without Suspense:
 * this hook keeps explicit `loading` and `error` state and safely ignores
 * stale async results after unmount or dependency changes.
 *
 * @param promiseFactory - Function that creates the request promise.
 * @param options - Hook behaviour options.
 * @param deps - Explicit dependency list that controls automatic re-resolution, similar to `useMemo`.
 * @returns Current async value, loading flag, error, and manual resolvers.
 */
export const usePromiseValue = <T extends unknown>(
    promiseFactory: () => Promise<T>,
    options: UsePromiseValueOptions<T>,
    deps: ReadonlyArray<unknown> = []
): UsePromiseValueResult<T> => {
    const { initialValue, enabled = true, clearValueWhenDisabled = true }: UsePromiseValueOptions<T> = options
    const [value, setValue] = useState<T>(initialValue)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<Nullable<Error>>(null)
    const requestIdRef = useRef<number>(0)
    const previousDepsRef = useRef<Nullable<ReadonlyArray<unknown>>>(null)

    const resolve = useCallback(async (): Promise<void> => {
        if (!enabled) {
            if (clearValueWhenDisabled) {
                setValue(initialValue)
            }

            setLoading(false)
            setError(null)

            return
        }

        const requestId: number = requestIdRef.current + 1
        requestIdRef.current = requestId
        setLoading(true)
        setError(null)

        try {
            const response: T = await promiseFactory()
            if (requestIdRef.current !== requestId) return

            setValue(response)

            setLoading(false)
        } catch (caughtError: unknown) {
            if (requestIdRef.current !== requestId) return

            const resolvedError: Error = caughtError instanceof Error ? caughtError : new Error('Request failed.')
            setError(resolvedError)

            setLoading(false)
        }
    }, [clearValueWhenDisabled, enabled, initialValue, promiseFactory])

    useEffect((): void => {
        const previousDeps: Nullable<ReadonlyArray<unknown>> = previousDepsRef.current
        const previousDepsValues: ReadonlyArray<unknown> = previousDeps ?? []
        const hasDepsChanged: boolean =
            IsVarEmpty(previousDeps) ||
            previousDepsValues.length !== deps.length ||
            deps.some((dep: unknown, index: number): boolean => !Object.is(dep, previousDepsValues[index]))

        if (!hasDepsChanged) return

        previousDepsRef.current = deps
        resolve().catch((): void => undefined)
    }, [deps, resolve])

    useEffect(
        (): (() => void) => (): void => {
            requestIdRef.current += 1
        },
        []
    )

    return { value, loading, error, resolve, refresh: resolve }
}
